import React, { useEffect, useState, useMemo } from "react";
import S from "../styles";

// Tokens — match storefront/styles
const ink = "#0A0A0F";
const paper = "#F4F2EC";
const white = "#FFFFFF";
const accent = "#0033FF";
const muted = "rgba(10,10,15,0.58)";
const success = "#22c55e";
const fontDisplay = "'Archivo Black', 'Archivo', sans-serif";
const fontMono = "'JetBrains Mono', monospace";

const STORAGE_KEY = "instant-quote-config-v1";

// Mirror of the storefront's lib/quote/pricing.ts + materials.ts defaults.
// Keep these in sync — when this page persists to the backend, the storefront
// will fetch the same shape.
const DEFAULT_CONFIG = {
  engine: {
    laborRatePerHour: 65,
    laborMinPerPart: 4,
    margin: 1.85,
    minOrderUSD: 35,
  },
  leadMultipliers: { rush: 1.55, standard: 1.0, economy: 0.85 },
  qtyDiscounts: [
    { minQty: 1, factor: 1.0 },
    { minQty: 10, factor: 0.95 },
    { minQty: 50, factor: 0.88 },
    { minQty: 100, factor: 0.78 },
    { minQty: 500, factor: 0.65 },
    { minQty: 1000, factor: 0.55 },
  ],
  processes: {
    FDM: { machineRate: 8, setupFee: 12 },
    SLA: { machineRate: 22, setupFee: 18 },
    SLS: { machineRate: 28, setupFee: 35 },
    MJF: { machineRate: 26, setupFee: 35 },
    LASER: { machineRate: 45, setupFee: 15 },
    UV: { machineRate: 30, setupFee: 20 },
  },
  finishingFees: {
    vaporSmoothing: 6,
    paint: 12,
    inserts: 4,
    assembly: 8,
  },
  materials: [
    // FDM
    { id: "fdm-pla", process: "FDM", name: "PLA", densityGCm3: 1.24, pricePerGramUSD: 0.05, infillFactor: 0.25, hoursPerCm3: 0.06, leadMin: 1, leadMax: 1 },
    { id: "fdm-petg", process: "FDM", name: "PETG", densityGCm3: 1.27, pricePerGramUSD: 0.06, infillFactor: 0.25, hoursPerCm3: 0.07, leadMin: 1, leadMax: 1 },
    { id: "fdm-abs", process: "FDM", name: "ABS", densityGCm3: 1.04, pricePerGramUSD: 0.06, infillFactor: 0.3, hoursPerCm3: 0.07, leadMin: 1, leadMax: 1 },
    { id: "fdm-asa", process: "FDM", name: "ASA", densityGCm3: 1.07, pricePerGramUSD: 0.07, infillFactor: 0.3, hoursPerCm3: 0.07, leadMin: 1, leadMax: 1 },
    { id: "fdm-tpu", process: "FDM", name: "TPU", densityGCm3: 1.21, pricePerGramUSD: 0.10, infillFactor: 0.4, hoursPerCm3: 0.12, leadMin: 1, leadMax: 2 },
    { id: "fdm-pc", process: "FDM", name: "PC (Polycarbonate)", densityGCm3: 1.20, pricePerGramUSD: 0.12, infillFactor: 0.35, hoursPerCm3: 0.10, leadMin: 1, leadMax: 2 },
    { id: "fdm-pa6-cf20", process: "FDM", name: "Carbon-filled PA6", densityGCm3: 1.16, pricePerGramUSD: 0.40, infillFactor: 0.7, hoursPerCm3: 0.13, leadMin: 2, leadMax: 3 },
    { id: "fdm-pa612-cf15", process: "FDM", name: "Carbon-filled PA612", densityGCm3: 1.15, pricePerGramUSD: 0.42, infillFactor: 0.7, hoursPerCm3: 0.13, leadMin: 2, leadMax: 3 },
    { id: "fdm-pps-cf10", process: "FDM", name: "Carbon-filled PPS", densityGCm3: 1.36, pricePerGramUSD: 0.85, infillFactor: 0.8, hoursPerCm3: 0.18, leadMin: 2, leadMax: 3 },
    { id: "fdm-petg-esd", process: "FDM", name: "ESD-safe PETG", densityGCm3: 1.28, pricePerGramUSD: 0.45, infillFactor: 0.4, hoursPerCm3: 0.10, leadMin: 2, leadMax: 3 },
    // SLA
    { id: "sla-grey-engineering", process: "SLA", name: "Grey Engineering Resin", densityGCm3: 1.17, pricePerGramUSD: 0.30, infillFactor: 1.0, hoursPerCm3: 0.20, leadMin: 3, leadMax: 3 },
    { id: "sla-clear", process: "SLA", name: "Clear Optical Resin", densityGCm3: 1.18, pricePerGramUSD: 0.35, infillFactor: 1.0, hoursPerCm3: 0.22, leadMin: 3, leadMax: 4 },
    { id: "sla-tough-pp", process: "SLA", name: "Tough Resin · PP-like", densityGCm3: 1.16, pricePerGramUSD: 0.32, infillFactor: 1.0, hoursPerCm3: 0.20, leadMin: 3, leadMax: 4 },
    { id: "sla-tough-abs", process: "SLA", name: "Tough Resin · ABS-like", densityGCm3: 1.16, pricePerGramUSD: 0.34, infillFactor: 1.0, hoursPerCm3: 0.20, leadMin: 3, leadMax: 4 },
    { id: "sla-rigid-glass-10k", process: "SLA", name: "Glass-Filled Ultra-Rigid Resin", densityGCm3: 1.62, pricePerGramUSD: 0.55, infillFactor: 1.0, hoursPerCm3: 0.25, leadMin: 3, leadMax: 5 },
    { id: "sla-high-temp", process: "SLA", name: "High-Temp Resin", densityGCm3: 1.20, pricePerGramUSD: 0.50, infillFactor: 1.0, hoursPerCm3: 0.22, leadMin: 3, leadMax: 5 },
    { id: "sla-flex-80a", process: "SLA", name: "Flexible 80A Resin", densityGCm3: 1.10, pricePerGramUSD: 0.40, infillFactor: 1.0, hoursPerCm3: 0.22, leadMin: 3, leadMax: 4 },
    { id: "sla-elastic-50a", process: "SLA", name: "Elastic 50A Resin", densityGCm3: 1.09, pricePerGramUSD: 0.45, infillFactor: 1.0, hoursPerCm3: 0.22, leadMin: 3, leadMax: 4 },
    { id: "sla-esd", process: "SLA", name: "ESD-Safe Resin", densityGCm3: 1.21, pricePerGramUSD: 0.55, infillFactor: 1.0, hoursPerCm3: 0.22, leadMin: 3, leadMax: 5 },
    // SLS
    { id: "sls-pa12", process: "SLS", name: "Nylon 12", densityGCm3: 1.01, pricePerGramUSD: 0.42, infillFactor: 1.0, hoursPerCm3: 0.18, leadMin: 3, leadMax: 5 },
    { id: "sls-pa12-tough", process: "SLS", name: "Nylon 12 Tough", densityGCm3: 1.01, pricePerGramUSD: 0.46, infillFactor: 1.0, hoursPerCm3: 0.18, leadMin: 3, leadMax: 5 },
    { id: "sls-pa12-gf", process: "SLS", name: "Nylon 12 GF", densityGCm3: 1.22, pricePerGramUSD: 0.55, infillFactor: 1.0, hoursPerCm3: 0.20, leadMin: 4, leadMax: 6 },
    { id: "sls-pa11", process: "SLS", name: "Nylon 11", densityGCm3: 1.04, pricePerGramUSD: 0.50, infillFactor: 1.0, hoursPerCm3: 0.18, leadMin: 4, leadMax: 5 },
    { id: "sls-pa11-cf", process: "SLS", name: "Nylon 11 CF", densityGCm3: 1.06, pricePerGramUSD: 0.85, infillFactor: 1.0, hoursPerCm3: 0.22, leadMin: 4, leadMax: 6 },
    { id: "sls-tpu-90a", process: "SLS", name: "TPU 90A", densityGCm3: 1.15, pricePerGramUSD: 0.65, infillFactor: 1.0, hoursPerCm3: 0.22, leadMin: 4, leadMax: 6 },
  ],
};

const PROCESS_KEYS = ["FDM", "SLA", "SLS", "MJF", "LASER", "UV"];

function loadConfig() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) return JSON.parse(raw);
  } catch {
    /* fall through */
  }
  return JSON.parse(JSON.stringify(DEFAULT_CONFIG));
}

function saveConfig(cfg) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(cfg));
  } catch (err) {
    console.error("instant-quote-config save failed", err);
  }
}

const cardStyle = {
  background: white,
  border: `2px solid ${ink}`,
  padding: 24,
};

const labelStyle = {
  display: "block",
  fontFamily: fontMono,
  fontSize: 11,
  letterSpacing: "0.08em",
  textTransform: "uppercase",
  color: muted,
  fontWeight: 700,
  marginBottom: 6,
};

const inputStyle = {
  width: "100%",
  padding: "8px 10px",
  fontFamily: fontMono,
  fontSize: 13,
  background: white,
  color: ink,
  border: `2px solid ${ink}`,
  borderRadius: 0,
  outline: "none",
  appearance: "none",
  WebkitAppearance: "none",
  MozAppearance: "none",
};

const sectionHeaderStyle = {
  fontFamily: fontDisplay,
  fontSize: 22,
  fontWeight: 900,
  letterSpacing: "-0.01em",
  textTransform: "uppercase",
  marginBottom: 16,
  color: ink,
};

const eyebrowStyle = {
  fontFamily: fontMono,
  fontSize: 11,
  letterSpacing: "0.1em",
  textTransform: "uppercase",
  color: accent,
  fontWeight: 700,
  marginBottom: 8,
  display: "flex",
  alignItems: "center",
  gap: 8,
};

export default function InstantQuotingPage() {
  const [config, setConfig] = useState(loadConfig);
  const [dirty, setDirty] = useState(false);
  const [saved, setSaved] = useState(false);
  const [previewQty, setPreviewQty] = useState(10);
  const [previewVolume, setPreviewVolume] = useState(15);

  useEffect(() => {
    if (saved) {
      const t = setTimeout(() => setSaved(false), 1800);
      return () => clearTimeout(t);
    }
  }, [saved]);

  const updateEngine = (key, val) => {
    setConfig((c) => ({ ...c, engine: { ...c.engine, [key]: val } }));
    setDirty(true);
  };

  const updateLead = (key, val) => {
    setConfig((c) => ({ ...c, leadMultipliers: { ...c.leadMultipliers, [key]: val } }));
    setDirty(true);
  };

  const updateProcess = (proc, key, val) => {
    setConfig((c) => ({
      ...c,
      processes: { ...c.processes, [proc]: { ...c.processes[proc], [key]: val } },
    }));
    setDirty(true);
  };

  const updateFinishing = (key, val) => {
    setConfig((c) => ({ ...c, finishingFees: { ...c.finishingFees, [key]: val } }));
    setDirty(true);
  };

  const updateMaterial = (id, key, val) => {
    setConfig((c) => ({
      ...c,
      materials: c.materials.map((m) => (m.id === id ? { ...m, [key]: val } : m)),
    }));
    setDirty(true);
  };

  const updateQtyTier = (idx, key, val) => {
    setConfig((c) => ({
      ...c,
      qtyDiscounts: c.qtyDiscounts.map((t, i) => (i === idx ? { ...t, [key]: val } : t)),
    }));
    setDirty(true);
  };

  const onSave = () => {
    saveConfig(config);
    setDirty(false);
    setSaved(true);
    // TODO: POST to /api/storefront/pricing-config when the backend route is wired.
    // For now, the storefront still uses its local defaults — once the Flask
    // endpoint exists, it'll pull from this saved config and the storefront
    // will fetch on /quote pages.
  };

  const onResetDefaults = () => {
    if (!window.confirm("Reset all values to factory defaults? This wipes any saved config.")) return;
    const fresh = JSON.parse(JSON.stringify(DEFAULT_CONFIG));
    setConfig(fresh);
    saveConfig(fresh);
    setDirty(false);
    setSaved(true);
  };

  // Live preview of a sample quote using current config.
  const sampleQuote = useMemo(() => {
    const m = config.materials.find((x) => x.process === "FDM" && x.name === "PLA") || config.materials[0];
    if (!m) return null;
    const proc = config.processes[m.process] || { machineRate: 25, setupFee: 15 };
    const weightG = previewVolume * m.densityGCm3 * m.infillFactor;
    const matCost = weightG * m.pricePerGramUSD;
    const machineHours = previewVolume * m.hoursPerCm3;
    const machineCost = machineHours * proc.machineRate;
    const laborCost = (config.engine.laborMinPerPart / 60) * config.engine.laborRatePerHour;
    const baseUnit = matCost + machineCost + laborCost;
    const margined = baseUnit * config.engine.margin;
    const tier = [...config.qtyDiscounts].sort((a, b) => b.minQty - a.minQty).find((t) => previewQty >= t.minQty);
    const factor = tier ? tier.factor : 1.0;
    const adjustedUnit = margined * factor;
    const subtotal = adjustedUnit * previewQty + proc.setupFee;
    const total = Math.max(config.engine.minOrderUSD, subtotal);
    return {
      material: m,
      process: m.process,
      weightG,
      matCost,
      machineCost,
      laborCost,
      machineHours,
      adjustedUnit,
      total,
      qtyFactor: factor,
    };
  }, [config, previewQty, previewVolume]);

  return (
    <div>
      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 24, flexWrap: "wrap", gap: 12 }}>
        <div>
          <h2 style={{ fontFamily: fontDisplay, fontSize: 32, fontWeight: 900, color: ink, letterSpacing: "-0.02em", textTransform: "uppercase", lineHeight: 1, marginBottom: 6 }}>
            Instant Quoting
          </h2>
          <p style={{ fontFamily: fontMono, fontSize: 12, color: muted, textTransform: "uppercase", letterSpacing: "0.06em" }}>
            Pricing engine config · drives nycmanufacturing.com /quote
          </p>
        </div>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
          {dirty && (
            <span style={{ fontFamily: fontMono, fontSize: 11, letterSpacing: "0.08em", textTransform: "uppercase", color: accent, fontWeight: 700 }}>
              ● Unsaved changes
            </span>
          )}
          {saved && (
            <span style={{ fontFamily: fontMono, fontSize: 11, letterSpacing: "0.08em", textTransform: "uppercase", color: success, fontWeight: 700 }}>
              ✓ Saved
            </span>
          )}
          <button
            onClick={onResetDefaults}
            style={{ ...S.clearBtn, padding: "10px 18px", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", fontSize: 12 }}
          >
            Reset Defaults
          </button>
          <button
            onClick={onSave}
            disabled={!dirty}
            style={{
              ...S.saveJobBtn,
              width: "auto",
              padding: "12px 24px",
              opacity: dirty ? 1 : 0.5,
              cursor: dirty ? "pointer" : "default",
            }}
          >
            Save Changes
          </button>
        </div>
      </div>

      {/* Live preview banner */}
      <div
        style={{
          ...cardStyle,
          background: paper,
          marginBottom: 24,
          borderColor: accent,
          borderWidth: 2,
          borderStyle: "solid",
        }}
      >
        <div style={eyebrowStyle}>◆ Live Preview · sample part</div>
        <div style={{ display: "grid", gridTemplateColumns: "200px 200px 1fr", gap: 20, alignItems: "end" }}>
          <div>
            <label style={labelStyle}>Volume (cm³)</label>
            <input
              type="number"
              value={previewVolume}
              min={0.1}
              step={0.5}
              onChange={(e) => setPreviewVolume(parseFloat(e.target.value) || 0)}
              style={inputStyle}
            />
          </div>
          <div>
            <label style={labelStyle}>Quantity</label>
            <input
              type="number"
              value={previewQty}
              min={1}
              onChange={(e) => setPreviewQty(parseInt(e.target.value) || 1)}
              style={inputStyle}
            />
          </div>
          {sampleQuote && (
            <div style={{ display: "flex", gap: 24, alignItems: "baseline", flexWrap: "wrap" }}>
              <Stat label="Material" val={sampleQuote.material.name} />
              <Stat label="Weight" val={`${sampleQuote.weightG.toFixed(1)}g`} />
              <Stat label="Machine" val={`${sampleQuote.machineHours.toFixed(2)}h`} />
              <Stat label="Per unit" val={`$${sampleQuote.adjustedUnit.toFixed(2)}`} />
              <Stat label="Total" val={`$${sampleQuote.total.toFixed(2)}`} accent big />
            </div>
          )}
        </div>
      </div>

      {/* Engine constants */}
      <div style={{ ...cardStyle, marginBottom: 16 }}>
        <div style={eyebrowStyle}>● Engine constants</div>
        <h3 style={sectionHeaderStyle}>Global rates &amp; margin</h3>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 16 }}>
          <Field
            label="Labor rate ($/hour)"
            value={config.engine.laborRatePerHour}
            onChange={(v) => updateEngine("laborRatePerHour", v)}
            type="number"
            step="1"
          />
          <Field
            label="Labor min per part"
            value={config.engine.laborMinPerPart}
            onChange={(v) => updateEngine("laborMinPerPart", v)}
            type="number"
            step="0.5"
            help="Setup + post-process minutes baseline"
          />
          <Field
            label="House margin (×)"
            value={config.engine.margin}
            onChange={(v) => updateEngine("margin", v)}
            type="number"
            step="0.05"
            help="Multiplier on (material + machine + labor)"
          />
          <Field
            label="Min order ($)"
            value={config.engine.minOrderUSD}
            onChange={(v) => updateEngine("minOrderUSD", v)}
            type="number"
            step="1"
            help="Floor we'll quote regardless of part size"
          />
        </div>
      </div>

      {/* Lead time multipliers */}
      <div style={{ ...cardStyle, marginBottom: 16 }}>
        <div style={eyebrowStyle}>● Lead time multipliers</div>
        <h3 style={sectionHeaderStyle}>Rush / Standard / Economy</h3>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 16 }}>
          <Field
            label="Rush (×)"
            value={config.leadMultipliers.rush}
            onChange={(v) => updateLead("rush", v)}
            type="number"
            step="0.05"
          />
          <Field
            label="Standard (×)"
            value={config.leadMultipliers.standard}
            onChange={(v) => updateLead("standard", v)}
            type="number"
            step="0.05"
          />
          <Field
            label="Economy (×)"
            value={config.leadMultipliers.economy}
            onChange={(v) => updateLead("economy", v)}
            type="number"
            step="0.05"
          />
        </div>
      </div>

      {/* Quantity discount tiers */}
      <div style={{ ...cardStyle, marginBottom: 16 }}>
        <div style={eyebrowStyle}>● Volume discount tiers</div>
        <h3 style={sectionHeaderStyle}>Pricing curve</h3>
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {config.qtyDiscounts.map((tier, i) => (
            <div key={i} style={{ display: "grid", gridTemplateColumns: "1fr 1fr 80px", gap: 16, alignItems: "center" }}>
              <Field
                label={`Tier ${i + 1} · min qty`}
                value={tier.minQty}
                onChange={(v) => updateQtyTier(i, "minQty", v)}
                type="number"
                step="1"
              />
              <Field
                label="Factor"
                value={tier.factor}
                onChange={(v) => updateQtyTier(i, "factor", v)}
                type="number"
                step="0.01"
                help={`Effective: ${(tier.factor * 100).toFixed(0)}%`}
              />
              <div style={{ paddingTop: 22, fontFamily: fontMono, fontSize: 11, color: muted, letterSpacing: "0.06em", textTransform: "uppercase" }}>
                {((1 - tier.factor) * 100).toFixed(0)}% off
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Process constants */}
      <div style={{ ...cardStyle, marginBottom: 16 }}>
        <div style={eyebrowStyle}>● Per-process</div>
        <h3 style={sectionHeaderStyle}>Machine rate &amp; setup fee</h3>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 16 }}>
          {PROCESS_KEYS.map((p) => (
            <div key={p} style={{ border: `2px solid ${ink}`, padding: 14 }}>
              <div style={{ fontFamily: fontDisplay, fontSize: 18, fontWeight: 900, letterSpacing: "-0.01em", textTransform: "uppercase", marginBottom: 12 }}>
                {p}
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                <Field
                  label="Machine $/hr"
                  value={config.processes[p]?.machineRate ?? 0}
                  onChange={(v) => updateProcess(p, "machineRate", v)}
                  type="number"
                  step="1"
                  compact
                />
                <Field
                  label="Setup fee ($)"
                  value={config.processes[p]?.setupFee ?? 0}
                  onChange={(v) => updateProcess(p, "setupFee", v)}
                  type="number"
                  step="1"
                  compact
                />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Finishing fees */}
      <div style={{ ...cardStyle, marginBottom: 16 }}>
        <div style={eyebrowStyle}>● Add-ons</div>
        <h3 style={sectionHeaderStyle}>Finishing fees ($/part)</h3>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 16 }}>
          <Field label="Vapor smoothing" value={config.finishingFees.vaporSmoothing} onChange={(v) => updateFinishing("vaporSmoothing", v)} type="number" step="1" />
          <Field label="Paint" value={config.finishingFees.paint} onChange={(v) => updateFinishing("paint", v)} type="number" step="1" />
          <Field label="Heat-staked inserts" value={config.finishingFees.inserts} onChange={(v) => updateFinishing("inserts", v)} type="number" step="1" />
          <Field label="Assembly" value={config.finishingFees.assembly} onChange={(v) => updateFinishing("assembly", v)} type="number" step="1" />
        </div>
      </div>

      {/* Materials library */}
      <div style={{ ...cardStyle, marginBottom: 16 }}>
        <div style={eyebrowStyle}>● Materials library</div>
        <h3 style={sectionHeaderStyle}>{config.materials.length} materials · density · $/g · infill · hours/cm³ · lead</h3>
        <MaterialsTable materials={config.materials} onChange={updateMaterial} />
      </div>

      <div
        style={{
          marginTop: 32,
          padding: "16px 20px",
          background: paper,
          border: `2px solid ${ink}`,
          fontFamily: fontMono,
          fontSize: 12,
          lineHeight: 1.6,
          color: ink,
        }}
      >
        <strong style={{ letterSpacing: "0.06em", textTransform: "uppercase" }}>Wiring status:</strong> Edits save to this browser only (localStorage). Once we add the
        backend route <code style={{ background: white, padding: "2px 6px" }}>PUT /api/storefront/pricing-config</code>, the storefront will fetch the live config on every <code style={{ background: white, padding: "2px 6px" }}>/quote</code> page load and use it as the source of truth.
      </div>
    </div>
  );
}

function Stat({ label, val, accent: isAccent, big }) {
  return (
    <div>
      <div style={{ fontFamily: fontMono, fontSize: 10, letterSpacing: "0.08em", textTransform: "uppercase", color: muted, fontWeight: 700 }}>
        {label}
      </div>
      <div
        style={{
          fontFamily: fontMono,
          fontSize: big ? 22 : 14,
          fontWeight: 700,
          color: isAccent ? accent : ink,
          marginTop: 2,
          letterSpacing: big ? "-0.02em" : 0,
        }}
      >
        {val}
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", step, compact, help }) {
  const handle = (e) => {
    const raw = e.target.value;
    if (type === "number") {
      const n = parseFloat(raw);
      onChange(isNaN(n) ? 0 : n);
    } else {
      onChange(raw);
    }
  };
  return (
    <div>
      <label style={{ ...labelStyle, fontSize: compact ? 10 : 11 }}>{label}</label>
      <input type={type} step={step} value={value} onChange={handle} style={inputStyle} />
      {help && (
        <div
          style={{
            marginTop: 4,
            fontFamily: fontMono,
            fontSize: 10,
            color: muted,
            letterSpacing: "0.04em",
          }}
        >
          {help}
        </div>
      )}
    </div>
  );
}

function MaterialsTable({ materials, onChange }) {
  const grouped = useMemo(() => {
    const map = {};
    materials.forEach((m) => {
      if (!map[m.process]) map[m.process] = [];
      map[m.process].push(m);
    });
    return map;
  }, [materials]);

  const tdStyle = { padding: "8px 6px", borderBottom: `1px solid rgba(10,10,15,0.12)` };
  const numInput = { ...inputStyle, padding: "6px 8px", fontSize: 12 };

  return (
    <div style={{ overflowX: "auto" }}>
      {Object.entries(grouped).map(([proc, list]) => (
        <div key={proc} style={{ marginBottom: 24 }}>
          <div style={{ fontFamily: fontMono, fontSize: 11, letterSpacing: "0.1em", textTransform: "uppercase", color: accent, fontWeight: 700, marginBottom: 8 }}>
            {proc} · {list.length} material{list.length !== 1 ? "s" : ""}
          </div>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12, fontFamily: fontMono }}>
            <thead>
              <tr style={{ borderBottom: `2px solid ${ink}` }}>
                <th style={{ ...tdStyle, textAlign: "left", color: muted, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", fontSize: 10 }}>Name</th>
                <th style={{ ...tdStyle, textAlign: "left", color: muted, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", fontSize: 10, width: 100 }}>Density g/cm³</th>
                <th style={{ ...tdStyle, textAlign: "left", color: muted, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", fontSize: 10, width: 90 }}>$ / gram</th>
                <th style={{ ...tdStyle, textAlign: "left", color: muted, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", fontSize: 10, width: 80 }}>Infill</th>
                <th style={{ ...tdStyle, textAlign: "left", color: muted, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", fontSize: 10, width: 90 }}>Hr / cm³</th>
                <th style={{ ...tdStyle, textAlign: "left", color: muted, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", fontSize: 10, width: 90 }}>Lead min</th>
                <th style={{ ...tdStyle, textAlign: "left", color: muted, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", fontSize: 10, width: 90 }}>Lead max</th>
              </tr>
            </thead>
            <tbody>
              {list.map((m) => (
                <tr key={m.id}>
                  <td style={{ ...tdStyle, fontWeight: 700, color: ink }}>{m.name}</td>
                  <td style={tdStyle}>
                    <input type="number" step="0.01" value={m.densityGCm3} onChange={(e) => onChange(m.id, "densityGCm3", parseFloat(e.target.value) || 0)} style={numInput} />
                  </td>
                  <td style={tdStyle}>
                    <input type="number" step="0.01" value={m.pricePerGramUSD} onChange={(e) => onChange(m.id, "pricePerGramUSD", parseFloat(e.target.value) || 0)} style={numInput} />
                  </td>
                  <td style={tdStyle}>
                    <input type="number" step="0.05" value={m.infillFactor} onChange={(e) => onChange(m.id, "infillFactor", parseFloat(e.target.value) || 0)} style={numInput} />
                  </td>
                  <td style={tdStyle}>
                    <input type="number" step="0.01" value={m.hoursPerCm3} onChange={(e) => onChange(m.id, "hoursPerCm3", parseFloat(e.target.value) || 0)} style={numInput} />
                  </td>
                  <td style={tdStyle}>
                    <input type="number" step="1" value={m.leadMin} onChange={(e) => onChange(m.id, "leadMin", parseInt(e.target.value) || 0)} style={numInput} />
                  </td>
                  <td style={tdStyle}>
                    <input type="number" step="1" value={m.leadMax} onChange={(e) => onChange(m.id, "leadMax", parseInt(e.target.value) || 0)} style={numInput} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}
    </div>
  );
}
